import { memo, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { Alert, Anchor, Box } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyDeliveryMethodWebApiHandler_deliveryMethod,
  useRentalManagerUserPropertyEditV2WebApiHandler_additionalEmail,
  useRentalManagerUserPropertyEditV2WebApiHandler_contactName,
  useRentalManagerUserPropertyEditV2WebApiHandler_email,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { REVIEW_PAGE_SECTIONS } from '../../../../constants/ListingDetailConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import DeliveryMethod from '../../../../model/DeliveryModel';

import { useAuth } from '../../../AuthProvider';
import { WizardNavigationProvider } from '../../../CreateListingPage/WizardNavigationProvider';
import AddEditPhoneNumber from '../../../PostingPath/AddEditPhoneNumber';
import ContactEmailDeliverabilityAlert from '../../../PostingPath/ContactEmailDeliverabilityAlert';
import ForRentByFieldSet from '../../../PostingPath/ForRentByFieldSet';
import RecommendationsAlert from '../../../PostingPath/RecommendationsAlert';
import RMWInput from '../../../RMWInput';
import type { DatarouterError } from '@zg-rentals/web-base';
const FIELD_NAME_OPTIONS = {
  contactName: 'contactName',
  contactEmail: 'contactEmail',
  additionalEmail: 'additionalEmail',
} as const;

const contactNameDefaultPrefillData = {
  name: FIELD_NAME_OPTIONS.contactName,
  value: 'Leasing Agent',
};

type Props = {
  onRecsClick: (recommendationType: string) => void;
};

const ContactContent = ({ onRecsClick }: Props): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails, isLoading: isLoadingListing } = useGetListingDetails(listingAlias);

  const prevFieldValueOnFocus = useRef({
    value: '',
    errorMessage: '',
  });

  const contactName = listingDetails?.contactName || '';
  const contactEmail = listingDetails?.email || '';
  const additionalEmail = listingDetails?.additionalEmail || '';
  const isFeed = listingDetails?.isFeed || false;
  const forRentBy = listingDetails?.forRentBy || '';
  const isDeliveredByEmail = listingDetails?.deliveryMethod?.isDeliveredByEmail || false;

  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const canViewContactEmailDeliverability = user?.features?.canViewContactEmailDeliverability ?? false;

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const canEditMFFeedContact =
    isFeed &&
    listingDetails?.restrictions?.isUpdateListingRestricted === false &&
    listingDetails?.isContactInfoEditable;
  const isDisabled = isFeed ? !canEditMFFeedContact : isInputFormDisabled;
  const shouldGetDeliveryMethod = Boolean(listingAlias) && isFeed && !isDisabled;

  const { data: deliveryMethodData, isLoading: isLoadingDeliveryMethod } =
    useRentalManagerUserPropertyDeliveryMethodWebApiHandler_deliveryMethod(
      { listingAlias },
      {
        enabled: shouldGetDeliveryMethod,
        staleTime: TimeConstants.SECONDS_10,
        meta: {
          context: {
            api: 'rental-manager',
            details: 'useRentalManagerUserPropertyDeliveryMethodWebApiHandler_deliveryMethod',
          },
          tags: ['users-properties'],
        },
      },
    );

  const deliveryMethod = deliveryMethodData ? DeliveryMethod(deliveryMethodData) : undefined;

  const isEmailDisabled =
    isDisabled || (shouldGetDeliveryMethod && isLoadingDeliveryMethod) || deliveryMethod?.isUpdateRestricted;
  const showDeliveryMethodMessage =
    contactEmail && canEditMFFeedContact && deliveryMethod?.displayName && deliveryMethod?.isUpdateRestricted;

  const {
    mutateAsync: updateContactName,
    error: contactNameError,
    reset: resetContactName,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_contactName,
    successToastMessage: 'Contact name updated',
    meta: {
      context: { api: 'contactName', details: { listingAlias } },
    },
  });

  const {
    mutateAsync: updateContactEmail,
    error: contactEmailError,
    reset: resetContactEmail,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_email,
    successToastMessage: 'Contact email updated',
    meta: {
      context: { api: 'email', details: { listingAlias } },
    },
  });

  const {
    mutateAsync: updateAdditionalEmail,
    error: additionalEmailError,
    reset: resetAdditionalEmail,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_additionalEmail,
    successToastMessage: 'Additional email updated',
    meta: {
      context: { api: 'additionalEmail', details: { listingAlias } },
    },
  });

  // ZRM-10180: if `contactName` is missing on landing, pre-fill with `Leasing Agent`
  useEffect(() => {
    if (!contactName && !isLoadingListing && listingAlias) {
      save({
        target: {
          name: contactNameDefaultPrefillData.name,
          value: contactNameDefaultPrefillData.value,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactName, isLoadingListing]);

  const save = async (eventOrValue: string | { target: { name: string; value: string } }) => {
    // Handle both string value and event object
    const fieldName =
      typeof eventOrValue === 'string' ? '' : (eventOrValue.target.name as keyof typeof FIELD_NAME_OPTIONS);
    const value = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;

    // Check if value actually changed
    if (prevFieldValueOnFocus.current.value === value) {
      return;
    }

    if (fieldName === FIELD_NAME_OPTIONS.contactName) {
      await updateContactName({
        postData: { contactName: value, listingAlias },
      });
    } else if (fieldName === FIELD_NAME_OPTIONS.contactEmail) {
      await updateContactEmail({ postData: { email: value, listingAlias } });
    } else if (fieldName === FIELD_NAME_OPTIONS.additionalEmail) {
      await updateAdditionalEmail({
        postData: { additionalEmail: value, listingAlias },
      });
    }
  };

  const handleFocus = (event: { target: { name: string; value: string } }) => {
    const { value } = event.target;
    prevFieldValueOnFocus.current.value = value;
  };

  return (
    <Box data-testid="ContactContent-Edit" css={{ display: 'flex', gap: '600', flexDirection: 'column' }}>
      <RecommendationsAlert sectionName={REVIEW_PAGE_SECTIONS.CONTACT} onRecsClick={(recKey) => onRecsClick(recKey)} />

      {isFeed && !isDisabled && (
        <Alert tone="warning">
          If you update your contact information here, future updates on your feed won&apos;t be applied. We recommend
          making updates in your feed.
        </Alert>
      )}

      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', lg: 'row' },
          gap: '400',
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '400',
            flex: { base: 'auto', lg: '1 1 50%' },
          }}
        >
          <RMWInput
            disabled={isDisabled}
            isRequired={!isDisabled}
            errorMessage={(contactNameError as DatarouterError)?.message}
            fieldName={FIELD_NAME_OPTIONS.contactName}
            initialValue={contactName}
            label="Name"
            clearFieldError={resetContactName}
            onBlur={save}
            onFocus={handleFocus}
          />

          <Box>
            <RMWInput
              disabled={isEmailDisabled}
              isRequired={!isEmailDisabled}
              errorMessage={(contactEmailError as DatarouterError)?.message}
              fieldName={FIELD_NAME_OPTIONS.contactEmail}
              initialValue={contactEmail}
              label="Email"
              clearFieldError={resetContactEmail}
              onBlur={save}
              onFocus={handleFocus}
            />
            {canViewContactEmailDeliverability && (
              <Box css={{ marginTop: '400' }}>
                <ContactEmailDeliverabilityAlert />
              </Box>
            )}
          </Box>

          {showDeliveryMethodMessage && (
            <Alert tone="warning">
              {`Your lead delivery method is a custom integration ${deliveryMethod?.displayName}.`} So, it can’t be
              edited here. Reach out to&nbsp;
              {deliveryMethod?.supportEmail ? (
                <>
                  <Anchor href={`mailto:${deliveryMethod?.supportEmail}`}>{deliveryMethod?.supportEmail}</Anchor>
                  &nbsp;to update.
                </>
              ) : (
                <>{deliveryMethod?.displayName} Support for assistance.</>
              )}
            </Alert>
          )}

          {isDeliveredByEmail && (
            <RMWInput
              disabled={isInputFormDisabled}
              errorMessage={(additionalEmailError as DatarouterError)?.message}
              fieldName={FIELD_NAME_OPTIONS.additionalEmail}
              initialValue={additionalEmail}
              label="CC email (an additional email)"
              clearFieldError={resetAdditionalEmail}
              onBlur={save}
              onFocus={handleFocus}
            />
          )}
        </Box>

        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            flex: { base: 'auto', lg: '1 1 50%' },
          }}
        >
          <AddEditPhoneNumber isInputFormDisabled={isDisabled} />
        </Box>
      </Box>

      {(!isFeed || forRentBy) && (
        <WizardNavigationProvider
          value={{ goToStep: () => {}, currentStepKey: '', reportError: () => {}, clearError: () => {} }}
        >
          <ForRentByFieldSet />
        </WizardNavigationProvider>
      )}
    </Box>
  );
};

export default memo(ContactContent);
